import _cloneDeep from 'lodash.clonedeep'
import { Alert } from '../../contexts/ApplicationSuiteProvider'
import { normalizeSchedule } from '../backtestSchedule'

/**
 * Alert types that must never survive duplication.
 *
 * `webhook` is the auto-trade flag, not a webhook URL — it drives
 * `isAutotraderActive` (AlertCard.tsx, SettingsTab.tsx) and counts toward the
 * auto-trader limits (autoTradeLimits.ts). Carrying it over
 * would consume an auto-trader slot for an alert with no broker account.
 *
 * `exitTradingPlan` marks a companion exit plan rather than a user-facing
 * alert; those are filtered out of the list entirely (AlertContext.tsx), so
 * this is belt-and-braces.
 */
const NON_TRANSFERABLE_ALERT_TYPES: Alert['alertTypes'] = ['webhook', 'exitTradingPlan']

/**
 * Builds a new-alert draft from an existing alert.
 *
 * Deliberately an allow-list rather than a spread: `tradingPlans` docs are also
 * written by executeAlert.ts, setAlertAutoTradeEnabled.ts and the GTF seeding
 * code, so a spread would drag trigger timestamps and broker runtime state into
 * the copy. Optional keys are omitted rather than set to `undefined` because
 * Firestore rejects undefined field values.
 *
 * The duplicate keeps the source's `pattern.id`: two alerts sharing one pattern
 * on different symbols is a supported state, since syncAlertPatternVersion.ts
 * re-applies each alert's own symbol on every pattern-version sync.
 */
export function buildDuplicateAlert(
  source: Alert,
  { canActivateAlerts }: { canActivateAlerts: boolean }
): Alert {
  const duplicate: Alert = {
    id: '',
    name: `${source.name} (Copy)`,
    timeRanges: _cloneDeep(source.timeRanges ?? []),
    timezone: source.timezone,
    daysOfWeek: [...(source.daysOfWeek ?? [])],
    schedule: normalizeSchedule(source),
    alertTypes: (source.alertTypes ?? []).filter(
      (alertType) => !NON_TRANSFERABLE_ALERT_TYPES.includes(alertType)
    ),
    active: canActivateAlerts ? !!source.active : false,
  }

  if (source.pattern) duplicate.pattern = _cloneDeep(source.pattern)
  if (source.symbol) duplicate.symbol = source.symbol
  if (source.user) duplicate.user = { ...source.user }
  if (source.autoUpdatePattern !== undefined) {
    duplicate.autoUpdatePattern = source.autoUpdatePattern
  }

  // Attribution is preserved so a duplicate of a backtest-derived alert keeps
  // its "View Backtest" link to the original run.
  if (source.from) duplicate.from = source.from
  if (source.backtestId) duplicate.backtestId = source.backtestId
  if (source.backtestGcid) duplicate.backtestGcid = source.backtestGcid

  // Trade parameters carry over; live broker wiring does not. The duplicate
  // arrives notify-only and must be armed deliberately.
  if (source.trading?.options) {
    duplicate.trading = {
      enabled: false,
      options: _cloneDeep(source.trading.options),
    }
  }

  return duplicate
}
